import React, { useMemo, useState } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails, Typography, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Tooltip,
  Box, Collapse, Stack
} from '@mui/material';
import { ExpandMore, Replay, KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import apiService from '../../services/api';
import { createLogger, adminLogContext } from '../../utils/logger';
import { addUserActionBreadcrumb } from '../../utils/sentryTracking';
import {
  RG_STAGE_LABELS,
  aggregateStageRuns,
  formatInt,
  formatUsd,
  formatTokens,
  formatDuration,
  getStageDetailSummary,
  getRunDetailSummary,
  getTotalTokens,
} from '../../utils/reportGenStages';

const logger = createLogger('TaskHistory');

const getStatusChipColor = (status) => {
  switch (status) {
    case 'failed': return 'error';
    case 'completed': return 'success';
    case 'cancelled': return 'default';
    case 'partial': return 'warning';
    case 'running': return 'primary';
    case 'success': return 'success';
    default: return 'default';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString();
};

const StageMetricsCells = ({ stage, fontSize = '0.75rem' }) => {
  const failed = Number(stage?.items_failed || 0);
  const skipped = Number(stage?.items_skipped || 0);
  const hasTokens = stage?.input_tokens || stage?.output_tokens || stage?.thinking_tokens;
  const hasCost = Number(stage?.cost_usd) > 0;

  return (
    <>
      <TableCell align="right" sx={{ fontSize }}>{formatInt(stage?.items_total)}</TableCell>
      <TableCell align="right" sx={{ fontSize, color: 'success.main' }}>{formatInt(stage?.items_succeeded)}</TableCell>
      <TableCell align="right" sx={{ fontSize, color: skipped > 0 ? 'text.secondary' : 'inherit' }}>
        {skipped > 0 ? formatInt(skipped) : '—'}
      </TableCell>
      <TableCell align="right" sx={{ fontSize, color: failed > 0 ? 'error.main' : 'inherit' }}>
        {failed > 0 ? formatInt(failed) : '—'}
      </TableCell>
      <TableCell align="right" sx={{ fontSize }}>{hasTokens ? formatTokens(stage.input_tokens) : '—'}</TableCell>
      <TableCell align="right" sx={{ fontSize }}>{hasTokens ? formatTokens(stage.output_tokens) : '—'}</TableCell>
      <TableCell align="right" sx={{ fontSize }}>{stage?.thinking_tokens ? formatTokens(stage.thinking_tokens) : '—'}</TableCell>
      <TableCell align="right" sx={{ fontSize }}>{hasCost ? formatUsd(stage.cost_usd) : '—'}</TableCell>
      <TableCell align="right" sx={{ fontSize }}>{formatDuration(stage?.duration_ms) || '—'}</TableCell>
    </>
  );
};

const RunDetailTable = ({ runs }) => (
  <Table size="small" sx={{ ml: 3, mb: 1, bgcolor: 'background.paper' }}>
    <TableHead>
      <TableRow>
        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Run</TableCell>
        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Items</TableCell>
        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">OK</TableCell>
        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Skip</TableCell>
        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Fail</TableCell>
        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">In</TableCell>
        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Out</TableCell>
        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Think</TableCell>
        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Cost</TableCell>
        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Time</TableCell>
        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Detail</TableCell>
        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Status</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {runs.map((run, i) => (
        <TableRow key={run.id || i}>
          <TableCell sx={{ fontSize: '0.7rem' }}>{i + 1}</TableCell>
          <StageMetricsCells stage={run} fontSize="0.7rem" />
          <TableCell sx={{ fontSize: '0.7rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <Tooltip title={getRunDetailSummary(run) || ''}>
              <span>{getRunDetailSummary(run) || '—'}</span>
            </Tooltip>
          </TableCell>
          <TableCell sx={{ fontSize: '0.7rem' }}>
            <Chip label={run.status || '—'} size="small" variant="outlined"
              color={getStatusChipColor(run.status)} sx={{ height: 18, fontSize: '0.6rem' }} />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const AggregatedStageRow = ({ agg }) => {
  const [expanded, setExpanded] = useState(false);
  const hasRuns = agg.run_count > 1;
  const detail = getStageDetailSummary(agg);

  return (
    <>
      <TableRow
        hover={hasRuns}
        onClick={() => hasRuns && setExpanded(!expanded)}
        sx={{ cursor: hasRuns ? 'pointer' : 'default', '& > *': { borderBottom: expanded ? 0 : undefined } }}
      >
        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {hasRuns && (
              <IconButton size="small" sx={{ p: 0.25 }}>
                {expanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
              </IconButton>
            )}
            <span>{RG_STAGE_LABELS[agg.stage] || agg.stage}</span>
            {hasRuns && (
              <Chip label={`${agg.run_count}×`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
            )}
          </Box>
        </TableCell>
        <StageMetricsCells stage={agg} />
        <TableCell sx={{ fontSize: '0.75rem', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <Tooltip title={detail || ''}>
            <span>{detail || '—'}</span>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ fontSize: '0.75rem' }}>
          <Chip label={agg.status || '—'} size="small" variant="outlined"
            color={getStatusChipColor(agg.status)} sx={{ height: 20, fontSize: '0.65rem' }} />
        </TableCell>
      </TableRow>
      {hasRuns && (
        <TableRow>
          <TableCell colSpan={12} sx={{ py: 0, px: 1, bgcolor: 'background.default' }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <RunDetailTable runs={agg.runs} />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const StageDetail = ({ stages }) => {
  const aggregated = useMemo(() => aggregateStageRuns(stages), [stages]);

  if (!aggregated.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1, px: 2 }}>
        No stage data recorded for this task.
      </Typography>
    );
  }

  return (
    <TableContainer sx={{ maxWidth: '100%' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Stage</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">Items</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">OK</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">Skip</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">Fail</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">In</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">Out</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">Think</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">Cost</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">Time</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Summary</TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {aggregated.map((agg) => (
            <AggregatedStageRow key={agg.stage} agg={agg} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export { StageDetail };

const ExpandableRow = ({ task, handleRestart }) => {
  const [open, setOpen] = useState(false);
  const stages = task.stages || [];
  const aggregated = useMemo(() => aggregateStageRuns(stages), [stages]);
  const hasStages = aggregated.length > 0;
  const rawRunCount = stages.length;

  return (
    <>
      <TableRow
        sx={{ cursor: hasStages ? 'pointer' : 'default', '& > *': { borderBottom: open ? 0 : undefined } }}
        hover
        onClick={() => hasStages && setOpen(!open)}
      >
        <TableCell sx={{ width: 28, p: 0.5 }}>
          {hasStages && (
            <IconButton size="small" sx={{ p: 0.25 }}>
              {open ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{task.county_name}</TableCell>
        <TableCell>
          <Chip label={task.status} color={getStatusChipColor(task.status)} size="small" />
        </TableCell>
        <TableCell sx={{ fontSize: '0.75rem' }}>{formatDate(task.completed_at)}</TableCell>
        <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <Tooltip title={task.status_message || ''}>
            <span>{task.status_message || ''}</span>
          </Tooltip>
        </TableCell>
        <TableCell align="right">{formatInt(task.files_to_process)}</TableCell>
        <TableCell align="right">{formatInt(task.total_reports_generated)}</TableCell>
        <TableCell align="right">
          <Tooltip title={rawRunCount > aggregated.length ? `${aggregated.length} pipeline stages (${rawRunCount} DB rows)` : `${aggregated.length} pipeline stages`}>
            <span>{aggregated.length || ''}</span>
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          <Tooltip title={`in: ${formatTokens(task.total_input_tokens)} | out: ${formatTokens(task.total_output_tokens)} | think: ${formatTokens(task.total_thinking_tokens)}`}>
            <span>{formatTokens(getTotalTokens(task))}</span>
          </Tooltip>
        </TableCell>
        <TableCell align="right">{formatUsd(task.total_cost)}</TableCell>
        <TableCell>
          {task.status === 'failed' && (
            <Tooltip title="Restart Task">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRestart(task.id); }}>
                <Replay fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
      {hasStages && (
        <TableRow>
          <TableCell colSpan={12} sx={{ py: 0, px: 2, bgcolor: 'action.hover' }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ py: 1 }}>
                <StageDetail stages={stages} />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const TaskHistory = ({ tasks = [], onRefresh }) => {
  const handleRestart = async (taskId) => {
    addUserActionBreadcrumb('Restart report generation task', {
      view: 'admin_reports',
      action: 'restart_task',
      target_task_id: String(taskId),
    });
    try {
      await apiService.restartTask(taskId);
      if (onRefresh) onRefresh();
    } catch (error) {
      logger.apiFailure('Failed to restart task', adminLogContext('file_and_report_management', {
        action: 'restart_task',
        target_task_id: String(taskId),
      }), error);
    }
  };

  const safeTasks = useMemo(() => {
    if (!Array.isArray(tasks)) return [];
    return tasks.filter((t) => t && typeof t === 'object');
  }, [tasks]);

  return (
    <Accordion defaultExpanded={false}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Task History</Typography>
          <Chip label={safeTasks.length} size="small" variant="outlined" />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 28 }} />
                <TableCell sx={{ fontWeight: 600 }}>County</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Completed</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Files</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Reports</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right" title="Pipeline stages (aggregated)">Stages</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Tokens</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Cost</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {safeTasks.map((task) => (
                <ExpandableRow key={task.id} task={task} handleRestart={handleRestart} />
              ))}
              {safeTasks.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    No historical tasks found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
};

export default TaskHistory;
